import React, { useEffect } from "react";
import { Typography } from "@mui/material";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { FormProvider } from "../../../provider/Form/FormProvider";
import { useLocation, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/reactQuery/reactQueryService";
import LegalbirdLoader from "../../ContentLoader/LegalbirdLoader";
import { Channel } from "../../../types/Entities/Messaging/Channel";
import MessageFormStepInner from "./MessageFormStepInner";
import { MessageCreateData } from "../../../types/Messaging/MessageCreateData";
import { TiptapEditorProvider } from "../../Tiptap/TiptapEditorProvider";
import { useCurrentEditor } from "@tiptap/react";

type NoteFormStepProps = {
  product: AbstractCase;
  setCurrentStep: Function;
  setMessageCreateData: Function;
  messageCreateData: MessageCreateData | null;
};

export default function MessageFormStep(props: NoteFormStepProps) {
  return (
    <TiptapEditorProvider>
      <MessageFormStepForm {...props} />
    </TiptapEditorProvider>
  );
}

function MessageFormStepForm({ setCurrentStep, product, setMessageCreateData, messageCreateData = null }: NoteFormStepProps) {
  const { editor } = useCurrentEditor();
  const [searchParams] = useSearchParams();

  const location = useLocation();
  const initializationData = location.state;

  const channelFilter = {
    case: product.id,
  };

  const { data: caseChannels, isLoading: isLoadingChannels } = useQuery({
    queryKey: queryKeys.collection("/channels", channelFilter),

    queryFn: () =>
      fetchCollection("/channels", channelFilter)
  });

  useEffect(() => {
    if (!initializationData || !caseChannels) {
      return;
    }

    editor?.commands.setContent(initializationData.editor);

    onSubmit({ overwriteMessageText: editor?.getHTML() });
    location.state = null;
  }, [initializationData, caseChannels]);

  useEffect(() => {
    if (messageCreateData?.messageText) {
      editor?.commands.setContent(messageCreateData.messageText);
    }
  }, []);

  const conversationId = searchParams.get("conversationId");
  const isConversationAnswer = !!conversationId;

  const onSubmit = ({ values, overwriteMessageText }: Record<string, any>) => {
    const messageText = overwriteMessageText || editor?.getHTML();
    const mainChannel = caseChannels.find((channel: Channel) => channel.type === "all");

    if (!isConversationAnswer && !mainChannel) {
      return;
    }

    const conversationPayload = isConversationAnswer
      ? "/conversations/" + conversationId
      : {
          subject: values.subject,
          type: values.type,
          channel: "/channels/" + mainChannel.id,
        };

    const createMessageData = {
      messageText,
      conversation: conversationPayload,
    };

    setMessageCreateData(createMessageData);
    setCurrentStep((currentStep: number) => {
      return currentStep + 1;
    });
  };

  const initialValues = {
    type: product.isCustomerDataCompleted ? "" : "technicalQuestion",
    subject: "",
    message: ""
  };

  if (isLoadingChannels) {
    return <LegalbirdLoader />;
  }

  if (!caseChannels) {
    return <Typography align={"center"}>Es ist ein technischer Fehler aufgetreten, bitte melden Sie sich bei unserem Kundendienst.</Typography>;
  }

  return (
    <FormProvider initialValues={initialValues} submitAction={onSubmit}>
      <MessageFormStepInner product={product} isConversationAnswer={isConversationAnswer} />
    </FormProvider>
  );
}
